// src/components/AlbumPageTemplates/Timeline.jsx
import React from 'react';

// Prefer meta.days (built by the album builder). Fall back to any items[] we get.
function toDays(meta, items) {
  if (meta?.days && Array.isArray(meta.days) && meta.days.length) return meta.days;
  if (Array.isArray(items) && items.length) {
    // Fallback: adapt items like {dateLabel, placeLabel} -> {when, places[]}
    return items.map(it => ({
      when: it.when || it.dateLabel || '',
      places: Array.isArray(it.places) ? it.places
             : (it.placeLabel ? [it.placeLabel] : [])
    }));
  }
  return [];
}

// Date formatting function
function formatDate(dateString, format) {
  if (!dateString) return '';
  
  try {
    // Parse MM/DD/YYYY format (the format from the builder)
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString; // Return as-is if not in expected format
    
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    if (isNaN(month) || isNaN(day) || isNaN(year)) return dateString;
    
    const date = new Date(year, month - 1, day);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[date.getMonth()];
    
    // Format according to user's preference
    switch(format) {
      case 'MM/DD/YYYY':
        return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      case 'DD/MM/YYYY':
        return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      case 'Month DD, YYYY':
        return `${monthName} ${day}, ${year}`;
      case 'DD Month YYYY':
        return `${day} ${monthName} ${year}`;
      default:
        return dateString; // Return original if format not recognized
    }
  } catch (e) {
    return dateString; // Return original if any error
  }
}

// Title-case for place names like "dubrovnik, croatia" -> "Dubrovnik, Croatia"
function toTitleCasePlace(str = '') {
  if (!str) return '';
  const SMALL = new Set(['and','or','the','a','an','of','in','on','at','to','for','by','de','la','le','du','da','do','dos','das']);

  // Split by commas first so "city, country" keeps comma spacing intact
  return str.split(',').map((segment, segIdx) => {
    const words = segment.trim().split(/\s+/).map((w, i, arr) => {
      const lower = w.toLowerCase();

      // Preserve all-caps & alphanumerics (e.g., "USA", "B1")
      if (/^[A-Z0-9\-]+$/.test(w)) return w;

      // Handle hyphenated words: "rio-de-janeiro" -> "Rio-de-Janeiro"
      const hy = lower.split('-').map((part, hi) => {
        if (!part) return part;
        // Always capitalize first/last word in a segment; otherwise keep small words lowercase
        const isEdge = i === 0 || i === arr.length - 1;
        if (!isEdge && SMALL.has(part)) return part;
        // O'Connor / L'Amore
        return part.replace(/^([a-z])/, (m, c) => c.toUpperCase())
                   .replace(/(^|\b|')([a-z])/g, (_, p, c) => p + c.toUpperCase());
      }).join('-');

      return hy;
    });

    return words.join(' ');
  }).join(', ');
}


export default function Timeline({ meta = {}, items = [] }) {
  const days = toDays(meta, items);

  // Read user's style preferences from meta.timelineStyle
  const style = meta.timelineStyle || {};
  const fontSize = style.fontSize || 'medium';
  const dateFormat = style.dateFormat || 'MM/DD/YYYY';
  const placeColor = style.placeColor || '#000000';  // Changed to black for visibility
  const dateColor = style.dateColor || '#000000';    // Changed to black for visibility
  const fontWeight = style.fontWeight || 'normal';
  
  // Map font size to CSS values
  const fontSizeMap = {
    small: '0.875rem',   // 14px
    medium: '1rem',      // 16px (default)
    large: '1.125rem'    // 18px
  };
  const baseFontSize = fontSizeMap[fontSize] || fontSizeMap.medium;

  // Simple two-column layout when many rows
  const cols = days.length > 12 ? 2 : 1;
  const perCol = Math.ceil(days.length / cols);
  const columns = Array.from({ length: cols }, (_, i) =>
    days.slice(i * perCol, (i + 1) * perCol)
  );

  return (
    <div style={{ position: 'absolute', inset: 0, padding: 24, display: 'flex', flexDirection: 'column' }}>
      {/* Title - Made very visible with black color and bold weight */}
      <div style={{ 
        fontSize: '1.5rem',        // 24px - larger and more prominent
        fontWeight: 700,           // Extra bold
        marginBottom: 16,
        color: '#000000',          // Pure black for maximum visibility
        letterSpacing: '-0.01em'   // Slightly tighter for modern look
      }}>
        Trip Timeline
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 18 }}>
        {columns.map((col, ci) => (
          <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {col.map((d, i) => {
              // Get places array - each place should be on its own line
              const places = Array.isArray(d.places) ? d.places : (d.places ? [d.places] : []);
              
              return (
                <div key={i}
                     style={{
                       display: 'grid',
                       gridTemplateColumns: '120px 1fr',
                       gap: 12,
                       alignItems: 'start', // Changed from 'baseline' to 'start' for multi-line support
                     }}>
                  {/* Date column */}
                  <div style={{ 
                    fontWeight: fontWeight === 'bold' ? 700 : 400,
                    fontSize: baseFontSize,
                    color: dateColor,
                    paddingTop: '0.1em' // Slight padding to align better with first place
                  }}>
                    {formatDate(d.when || '', dateFormat)}
                  </div>
                  
                  {/* Places column - each place on its own line */}
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.25em', // Small gap between places
                    fontSize: baseFontSize,
                    color: placeColor,
                    fontWeight: fontWeight === 'bold' ? 700 : 400,
                    lineHeight: 1.4
                  }}>
                    {places.map((place, idx) => (
                      <div key={idx}>{toTitleCasePlace(place)}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}